/**
 * Route guard for everything behind authentication.
 *
 * Per `aidlc-docs/construction/auth-redesign/functional-design/frontend-components.md § 6`
 * (ENG-35588 — extends the original guard with workspace-less handling).
 *
 * Decision tree:
 *   - `loading`: render a centered spinner. The window is short (a
 *     single `/api/me` round-trip) but blocking is safer than flashing
 *     the login page for a millisecond.
 *   - `anon`: navigate to `/login?redirect=<current path + search>` so
 *     the post-login redirect can deep-link.
 *   - `authenticated` + `workspace !== null`: render the child route's
 *     `<Outlet />`.
 *   - `authenticated` + `workspace === null` (workspace-less session,
 *     ENG-35588): redirect to `/no-workspace`. The `/no-workspace`
 *     route itself bypasses this redirect (it renders the create form).
 */

import { useAuth } from "@cometchat-ai/auth/client";
import { Spinner } from "@cometchat-ai/ui-dashboard";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router";

export function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation("auth");

  if (auth.status === "loading") {
    return (
      <div
        className="flex min-h-dvh items-center justify-center"
        data-testid="auth-bootstrap-loading"
      >
        <Spinner aria-label={t("bootstrap.loading")} />
      </div>
    );
  }

  if (auth.status === "anon") {
    const redirect = `${location.pathname}${location.search}`;
    const target = redirect && redirect !== "/" ? `?redirect=${encodeURIComponent(redirect)}` : "";
    return <Navigate to={`/login${target}`} replace />;
  }

  // Workspace-less session (ENG-35588) → route to onboarding unless the
  // user is already on the create-workspace screen.
  if (auth.workspace === null && location.pathname !== "/no-workspace") {
    return <Navigate to="/no-workspace" replace />;
  }

  return <Outlet />;
}
